//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Grid, Typography } from '@mui/material'
import React, { ReactElement, useEffect } from 'react'

import ButtonAction from '@react/components/elements/ButtonAction'

import { ETH_ADDR_ZERO, EthAddr } from '@shared/SchemaTypes'

import { useOtChainState, useOtSelector } from '@react/redux/store'

import { AddressPill } from '@react/components/addresses/AddressPill'
import { SectionHeader } from '@react/components/common/SectionHeader'
import { poolCrossChainTransferFetch } from '@react/fetchers/PoolFetchers'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { logOT } from '@shared/loggingShared'
import { PoolCrossChainTransferCTO } from '@shared/PoolCrossChainDataCTO'

export default function AdminApproveTransferDestination(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props
  const [poolCrossChainTransferCTO, setPoolCrossChainTransferCTO] = React.useState<
    PoolCrossChainTransferCTO | undefined
  >(undefined)
  const poolBaseOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO])

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    chainConfiguration: { chainId, providerReadOnly },
  } = useOtChainState()

  useEffect(() => {
    if (poolAddr && chainId && serverApiBaseUrl && providerReadOnly) {
      poolCrossChainTransferFetch(serverApiBaseUrl, restAPIKey, poolAddr).then(
        (_poolCrossChainTransferCTO: PoolCrossChainTransferCTO | undefined) => {
          setPoolCrossChainTransferCTO(_poolCrossChainTransferCTO)
        }
      )
    }
  }, [chainId, serverApiBaseUrl, providerReadOnly, poolAddr])

  const handleCancel = (): void => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  if (!poolCrossChainTransferCTO) {
    return <Typography variant="h2">Loading ....</Typography>
  }

  logOT.info('poolOffchainDataCTO?.crossChainTransfer', poolCrossChainTransferCTO)
  const handleTransferRequest = async (): Promise<void> => {
    performPoolCrossChainApproveDestination()
      .then(() => {
        const event = new Event('modalClose')
        window.dispatchEvent(event)
      })
      .catch((e) => {
        logOT.error(e)
      })
  }

  async function performPoolCrossChainApproveDestination() {
    throw new Error('TODO V5 Not implemented')
  }
  // async function performPoolCrossChainApproveDestination() {
  //   const fetchedPoolCrossTrainTransferCTO: PoolCrossChainTransferCTO | undefined = await poolCrossChainTransferFetch(
  //     serverApiBaseUrl,
  //     restAPIKey,
  //     poolAddr
  //   )
  //   if (!fetchedPoolCrossTrainTransferCTO) {
  //     throw new Error('fetchedPoolCrossTrainTransferCTO is undefined')
  //   }
  //   const sourceDeployment = DEPLOYMENT_CONFIGURATIONS.find(
  //     (config: StageConfiguration) => config.deploymentName === fetchedPoolCrossTrainTransferCTO.sourceDeploymentName
  //   )
  //   if (!sourceDeployment) {
  //     throw new Error('sourceDeployment is undefined ' + fetchedPoolCrossTrainTransferCTO.sourceDeploymentName)
  //   }
  //   logOT.info('sourceDeployment', sourceDeployment, fetchedPoolCrossTrainTransferCTO.sourceDeploymentName)

  //   fetchedPoolCrossTrainTransferCTO.transferStep = TransferStepNumbers.Destination_Approval_Pending
  //   fetchedPoolCrossTrainTransferCTO.transferStepName = TransferStepNames.Destination_Approval_Pending

  //   await poolCrossChainTransferPut(
  //     sourceDeployment.serverApiBaseUrl,
  //     sourceDeployment.restAPIKey,
  //     serverApiBaseUrl,
  //     restAPIKey,
  //     fetchedPoolCrossTrainTransferCTO
  //   )

  //   const poolControllerAddr = poolBaseOverviewCTO?.poolControllerAddr
  //   if (poolControllerAddr && signer && sourceDeployment) {
  //     const poolController = new ethers.Contract(poolControllerAddr, PoolControllerAbi, signer)

  //     if (poolBaseOverviewCTO?.poolType !== PoolTypeEnum.Dynamic) {
  //       const tx = await (
  //         poolController.connect(signer) as unknown as PoolController
  //       ).crossChainTransferApproveDestination(
  //         sourceDeployment.chainId,
  //         poolAddr,
  //         fetchedPoolCrossTrainTransferCTO.destinationWalletAddr,
  //         fetchedPoolCrossTrainTransferCTO.sourceWalletAddr,
  //         BigInt(fetchedPoolCrossTrainTransferCTO.amount)
  //       )
  //       await tx.wait(1)
  //     }
  //   } else {
  //     throw new Error('poolControllerAddr or signer is undefined')
  //   }

  //   fetchedPoolCrossTrainTransferCTO.transferStep = TransferStepNumbers.Destination_Approved
  //   fetchedPoolCrossTrainTransferCTO.transferStepName = TransferStepNames.Destination_Approved

  //   await poolCrossChainTransferPut(
  //     sourceDeployment.serverApiBaseUrl,
  //     sourceDeployment.restAPIKey,
  //     serverApiBaseUrl,
  //     restAPIKey,
  //     fetchedPoolCrossTrainTransferCTO
  //   )
  // }

  return (
    <Box py={2} px={2} width={500}>
      <Grid container spacing={6}>
        <Grid sx={{ marginTop: 2 }} item xs={12}>
          <SectionHeader>Source</SectionHeader>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ color: '#666' }} variant="h6">
            Source Chain ID
          </Typography>
          <Typography sx={{ fontWeight: 'bold' }} variant="small">
            {chainId}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ color: '#666' }} variant="h6">
            Source Vault Address
          </Typography>
          <AddressPill addr={poolAddr} />
        </Grid>
        <Grid sx={{ marginTop: 2 }} item xs={12}>
          <SectionHeader>Destination</SectionHeader>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ color: '#666' }} variant="h6">
            Destination Chain ID
          </Typography>
          <Typography sx={{ fontWeight: 'bold' }} variant="small">
            {poolCrossChainTransferCTO?.destinationChainId}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ color: '#666' }} variant="h6">
            Destination Account
          </Typography>
          <AddressPill addr={poolCrossChainTransferCTO?.destinationWalletAddr} />
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ color: '#666' }} variant="h6">
            Amount
          </Typography>
          <Typography sx={{ fontWeight: 'bold' }} variant="small">
            {erc20WeiToCommaDecString(poolCrossChainTransferCTO?.amount)} {poolBaseOverviewCTO?.liquidityTokenSymbol}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={12}>
          <ButtonAction
            data-testid={'TransferRequest'}
            sx={{ marginRight: '16px' }}
            variant={'primary'}
            onClick={handleTransferRequest}
          >
            Approve Transfer
          </ButtonAction>

          <ButtonAction variant={'tertiary'} onClick={handleCancel}>
            Cancel
          </ButtonAction>
        </Grid>
      </Grid>
    </Box>
  )
}
